import { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import type { AnalyticsData } from '@/lib/services/analytics';
import { loadAnalyticsService } from '@/lib/services/analytics-loader';
import { logger } from '@/lib/utils/logger';

export const useAnalytics = () => {
  const [data, setData] = useState<AnalyticsData | null>(null);
  const [loading, setLoading] = useState(true);

  const isActiveRef = useRef(true);
  const isFirstNavigationRef = useRef(true);

  const fetchAnalytics = useCallback(async () => {
    if (!isActiveRef.current) return;
    setLoading(true);
    try {
      const analytics = await loadAnalyticsService();
      const analyticsData = await analytics.getData();
      if (isActiveRef.current) {
        setData(analyticsData);
      }
    } catch (error) {
      logger.error(error, { source: 'useAnalytics.fetchAnalytics' });
    } finally {
      if (isActiveRef.current) {
        setLoading(false);
      }
    }
  }, []);

  const location = useLocation();

  useEffect(() => {
    const bootstrap = async () => {
      // Defer analytics initialization to not block critical rendering
      // Use requestIdleCallback if available, otherwise setTimeout
      const scheduleTask = (callback: () => void) => {
        if ('requestIdleCallback' in window) {
          window.requestIdleCallback(callback, { timeout: 2000 });
        } else {
          setTimeout(callback, 100);
        }
      };

      // Start session (non-blocking)
      scheduleTask(async () => {
        try {
          const analytics = await loadAnalyticsService();
          await analytics.startSession();
        } catch (error) {
          logger.warn('Impossible de démarrer la session analytics', { error });
        }
      });

      // Track initial page view (non-blocking)
      scheduleTask(async () => {
        try {
          const analytics = await loadAnalyticsService();
          await analytics.trackPageView(window.location.pathname);
        } catch (error) {
          logger.warn('Impossible d\'enregistrer la page vue initiale', { error });
        }
      });

      // Fetch analytics data (non-blocking)
      scheduleTask(async () => {
        await fetchAnalytics();
      });

      // Update performance metrics (non-blocking, delayed)
      scheduleTask(async () => {
        try {
          const analytics = await loadAnalyticsService();
          await analytics.updatePerformance();
        } catch (error) {
          logger.warn('Impossible de mettre à jour les performances', { error });
        }
      });
    };

    bootstrap();

    const interval = setInterval(fetchAnalytics, 30000);

    return () => {
      isActiveRef.current = false;
      clearInterval(interval);
    };
  }, [fetchAnalytics]);

  // Track subsequent route changes
  useEffect(() => {
    if (isFirstNavigationRef.current) {
      isFirstNavigationRef.current = false;
      return;
    }

    const track = async () => {
      try {
        const analytics = await loadAnalyticsService();
        await analytics.trackPageView(location.pathname);
        await fetchAnalytics();
      } catch (error) {
        logger.warn('Impossible d\'enregistrer la page vue', { error, path: location.pathname });
      }
    };
    void track();
  }, [location.pathname, fetchAnalytics]);

  const trackEvent = async (eventName: string, eventData: unknown = {}) => {
    logger.info('analytics:event', { eventName, eventData });
  };

  return {
    data,
    loading,
    trackEvent,
    refreshData: fetchAnalytics
  };
};
